import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  CrudMode,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { NscCollege, NscCollegeEditResource, NscCollegeViewResource, Permission } from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { NscCollegePageBody } from "./NscCollegePageBody";
import { NscCollegePageHeader } from "./NscCollegePageHeader";

const routeName = "nscCollege";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

class OptionalParams {
  identification?: string = "";
}

export const nscCollegePageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Post-Secondary",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export const NscCollegePage = () => {
  const ROOT_PERMISSION: Permission = Permission.StudentNscCollege;
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();

  const [identification] = useState<string | undefined>(optionalParams.identification);

  const [nscCollege, setNscCollege] = useState<NscCollege>({} as NscCollege);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [nscCollege] });
  const [viewResource, setViewResource] = useState<NscCollegeViewResource>({});
  const [editResource, setEditResource] = useState<NscCollegeEditResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    if (params.id) fetchGet(params.id);
    else fetchCreate(identification);

    fetchViewResource();
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") fetchEditResource();
  }, [params.crudMode]);

  const fetchGet = (id: number) => {
    ApiService.nscColleges.get(id).then((response) => {
      if (!id && identification) response.identification = identification;

      setNscCollege(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchCreate = (identification: string | undefined) => {
    ApiService.nscColleges.getCreate(identification).then((response) => {
      setNscCollege(response);
      resetUnsavedChanges([response]);
    });
  };

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${nscCollegePageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType })}`);

  const fetchViewResource = () => {
    ApiService.nscColleges.getViewResource().then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = () => {
    ApiService.nscColleges.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  const pageHeader = (
    <NscCollegePageHeader
      rootPermission={ROOT_PERMISSION}
      handle={nscCollegePageRoute.handle}
      crudMode={params.crudMode}
      reroute={reroute}
      fetch={fetchGet}
      nscCollege={nscCollege}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
    />
  );

  const pageBody = (
    <NscCollegePageBody
      crudMode={params.crudMode}
      nscCollege={nscCollege}
      setNscCollege={setNscCollege}
      validationErrors={validationErrors}
      viewResource={viewResource}
      editResource={editResource}
    />
  );

  return params.screenType === "standard" ? (
    <Page isForm header={pageHeader}>
      <Card>{pageBody}</Card>
    </Page>
  ) : (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="md"
      header={pageHeader}
      content={pageBody}
    />
  );
};
